import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useRef, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {
  DocumentReviewEffectiveRuleResponse,
  DocumentReviewEffectiveRulesResponse,
  DocumentResponse,
  SourceEntityRequest,
} from 'src/api/ProactApiClient';
import {
  DocumentReviewCompletionModeEnum,
  DocumentReviewTypeEnum,
  ModuleEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateDocumentNodeMutation,
  useGetDocumentQuery,
  useUpdateDocumentMutation,
} from 'src/redux/queries/document';
import {
  useCreateDocumentReviewRuleMutation,
  useDeleteDocumentReviewRuleMutation,
  useGetDocumentReviewRulesForFolderQuery,
  useUpdateDocumentReviewRuleMutation,
} from 'src/redux/queries/documentReview';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {Button} from 'src/design/atoms/Button';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {Pill} from 'src/design/atoms/Pill';
import {RadioGroupBox} from 'src/design/atoms/RadioGroupBox';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormPersonnelSelectModalV2} from 'src/app/forms/FormPersonnelSelectModal/FormPersonnelSelectModalV2';
import {SidebarLinkDocumentManager} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  MapDocumentResponseToDocumentCreateRequest,
  MapDocumentResponseToDocumentUpdateRequest,
} from 'src/pages/Modules/DocumentManagement/documentHelpers';
import {DocumentReviewTypeLabel} from 'src/pages/Modules/DocumentManagement/documentReviewHelpers';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useDocumentAncestors} from 'src/shared/hooks/useDocumentAncestors';
import {useDocumentManagementBreadcrumbs} from 'src/shared/hooks/useDocumentManagementBreadcrumbs';
import {useDocumentReviewsFeature} from 'src/shared/hooks/useDocumentReviewsFeature';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface DocumentFolderFormProps {
  id?: number;
  parentId?: string;
  mode: 'create' | 'edit';
}

const FOLDER_FORM_META: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'Folder name',
    type: 'text',
    required: true,
    description: 'A name to identify this folder.',
  },
];

const targetingField: IFormFieldV2 = {
  name: 'sourceEntities',
  label: 'Reviewers',
  type: 'person-select-multiple',
  required: true,
  description:
    'Choose who is assigned to review changes in this folder. If assigning by manager, department, or group, everyone belonging to them is assigned.',
  personOptions: {
    // Rule source entities are a plain list persisted on the rule
    // (DocumentReviewRuleCreateRequest.sourceEntities) — there is no
    // "select all X" concept on the wire for rules, unlike task targeting.
    hideSelectAll: true,
  },
};

const completionModeOptions: ISelectOption[] = [
  {
    id: DocumentReviewCompletionModeEnum.AnyOneApprover,
    label: 'Any one approver',
  },
  {id: DocumentReviewCompletionModeEnum.AllApprovers, label: 'All approvers'},
];

const toSourceEntityRequest = (
  effective?: DocumentReviewEffectiveRuleResponse
): SourceEntityRequest => ({
  entities: effective?.rule.sourceEntities ?? [],
});

interface ReviewRuleEditState {
  overrideMode: boolean;
  ownRuleId?: number;
  enabled: boolean;
  completionMode: DocumentReviewCompletionModeEnum;
  sourceEntities: SourceEntityRequest;
}

const RULE_TYPES = [
  DocumentReviewTypeEnum.Approval,
  DocumentReviewTypeEnum.Acknowledgment,
] as const;

type ReviewRuleType = (typeof RULE_TYPES)[number];

const effectiveRuleForType = (
  data: DocumentReviewEffectiveRulesResponse | undefined,
  reviewType: ReviewRuleType
): DocumentReviewEffectiveRuleResponse | undefined =>
  reviewType === DocumentReviewTypeEnum.Approval
    ? data?.approval
    : data?.acknowledgment;

const buildInitialRuleState = (
  folderId: number,
  effective?: DocumentReviewEffectiveRuleResponse
): ReviewRuleEditState => {
  const isOwnRule = effective?.rule.folderId === folderId;
  return {
    overrideMode: false,
    ownRuleId: isOwnRule ? effective?.rule.id : undefined,
    enabled: isOwnRule ? !!effective?.rule.active : false,
    completionMode:
      isOwnRule && effective?.rule.completionMode
        ? effective.rule.completionMode
        : DocumentReviewCompletionModeEnum.AnyOneApprover,
    sourceEntities: isOwnRule
      ? toSourceEntityRequest(effective)
      : {entities: []},
  };
};

const buildOverrideSeedValues = (
  effective: DocumentReviewEffectiveRuleResponse | undefined
): Pick<
  ReviewRuleEditState,
  'enabled' | 'completionMode' | 'sourceEntities'
> => ({
  enabled: !!effective?.rule.active,
  completionMode:
    effective?.rule.completionMode ??
    DocumentReviewCompletionModeEnum.AnyOneApprover,
  sourceEntities: toSourceEntityRequest(effective),
});

const isOwnEditable = (
  folderId: number,
  effective: DocumentReviewEffectiveRuleResponse | undefined,
  state: ReviewRuleEditState
): boolean =>
  !effective || effective.rule.folderId === folderId || state.overrideMode;

const sourceEntitiesKey = (value: SourceEntityRequest): string =>
  (value.entities ?? [])
    .map(entity => `${entity.entityType}:${entity.entityId}`)
    .sort()
    .join(',');

/**
 * Compares only the fields that are actually persisted for a rule. Ignores
 * `overrideMode`/`ownRuleId` (UI/identity bookkeeping, not rule content) so a
 * folder whose card was merely toggled into override mode — or an
 * already-own rule that was reopened and left unchanged — is never treated
 * as touched.
 */
const isRuleStateDirty = (
  current: ReviewRuleEditState,
  seed: ReviewRuleEditState
): boolean =>
  current.enabled !== seed.enabled ||
  current.completionMode !== seed.completionMode ||
  sourceEntitiesKey(current.sourceEntities) !==
    sourceEntitiesKey(seed.sourceEntities);

export interface ReviewRuleCardProps {
  folderId: number;
  reviewType: ReviewRuleType;
  /** Own or inherited effective rule for this type, if any. `undefined` means no rule applies at all. */
  effective?: DocumentReviewEffectiveRuleResponse;
  canEdit: boolean;
  state: ReviewRuleEditState;
  error?: string;
  onChange: (patch: Partial<ReviewRuleEditState>) => void;
  onRequestRemove: () => void;
  onCancelOverride: () => void;
  onEnterOverride: () => void;
}

/**
 * One review-rule (Approval or Acknowledgment) card on the folder edit page.
 * Purely presentational — edit state is lifted to `DocumentFolderForm`, which
 * orchestrates persisting it (alongside the folder itself and the other
 * rule) from a single global "Save Changes" action. Rule deletion is the one
 * exception: it stays an immediate, explicitly-confirmed action.
 *
 * Kept colocated in this file (rather than its own module under
 * `src/pages/...`) so it can reuse `FormPersonnelSelectModalV2` — `src/library`
 * is frozen to new importers, but this file is an existing, already-baselined
 * `src/library` file and may still be modified.
 */
const ReviewRuleCard: React.FC<ReviewRuleCardProps> = ({
  folderId,
  reviewType,
  effective,
  canEdit,
  state,
  error,
  onChange,
  onRequestRemove,
  onCancelOverride,
  onEnterOverride,
}) => {
  const navigate = useAppNavigate();

  const isOwnRule = effective?.rule.folderId === folderId;
  const isInheritedOnly = !!effective && !isOwnRule;

  const label = DocumentReviewTypeLabel(reviewType);

  const handleTargetingChange = (_name: string, value: SourceEntityRequest) => {
    onChange({sourceEntities: value});
  };

  if (isInheritedOnly && !state.overrideMode) {
    return (
      <SectionCard
        title={`${label} Review`}
        rightSection={<Pill text="Inherited" color="gray" />}
        data-testid={`review-rule-${reviewType}-inherited`}>
        <Box p="md">
          <Stack gap="sm">
            <Text size="sm">
              Inherited from{' '}
              <Anchor
                onClick={() =>
                  navigate(
                    PathUtility.DocumentManagerEditFolder(
                      effective!.sourceFolderId
                    )
                  )
                }>
                {effective?.sourceFolderName ?? 'parent folder'}
              </Anchor>
            </Text>
            {canEdit && (
              <Group>
                <Button variant="subtle" size="sm" onClick={onEnterOverride}>
                  Configure for this folder
                </Button>
              </Group>
            )}
          </Stack>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={`${label} Review`}
      rightSection={
        <Pill
          text={state.enabled ? 'Required' : 'Not required'}
          color={state.enabled ? 'green' : 'gray'}
        />
      }
      data-testid={`review-rule-${reviewType}`}>
      <Box p="md">
        <Stack gap="md">
          {state.overrideMode && (
            <InfoAlert>
              Configuring a rule for this folder overrides the rule inherited
              from {effective?.sourceFolderName ?? 'a parent folder'}.
            </InfoAlert>
          )}

          <Switch
            label={`Require ${label.toLowerCase()} in this folder`}
            color="green.8"
            checked={state.enabled}
            disabled={!canEdit}
            onChange={e => onChange({enabled: e.currentTarget.checked})}
          />

          {state.enabled && (
            <>
              <FormPersonnelSelectModalV2
                field={{...targetingField, disabled: !canEdit}}
                value={state.sourceEntities}
                error={error}
                onChange={handleTargetingChange}
              />

              {reviewType === DocumentReviewTypeEnum.Approval && (
                <RadioGroupBox
                  label="Completion"
                  helperText="How many approvers are required to approve a version before it publishes."
                  orientation="horizontal"
                  disabled={!canEdit}
                  options={completionModeOptions}
                  value={String(state.completionMode)}
                  onChangeCallback={value =>
                    onChange({
                      completionMode: Number(
                        value
                      ) as DocumentReviewCompletionModeEnum,
                    })
                  }
                />
              )}
            </>
          )}

          {canEdit && (isInheritedOnly || state.ownRuleId) && (
            <Group>
              {isInheritedOnly && state.overrideMode && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={onCancelOverride}>
                  Use inherited rule
                </Button>
              )}
              {state.ownRuleId && (
                <Button
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={onRequestRemove}>
                  Remove rule for this folder
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </Box>
    </SectionCard>
  );
};

export const DocumentFolderForm: React.FC<DocumentFolderFormProps> = ({
  id,
  parentId,
  mode,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedDocument, isLoading} = useGetDocumentQuery(
    {id: id!},
    {skip: id === undefined}
  );

  const [createDocumentNode] = useCreateDocumentNodeMutation();
  const [updateDocument] = useUpdateDocumentMutation();

  const form = useEntitySyncedForm({
    formMeta: FOLDER_FORM_META,
    entity: fetchedDocument,
    toFormData: toFormDataV2,
  });

  const hasReviewsFeature = useDocumentReviewsFeature();
  const documentsPerms = useModulePermissions(ModuleEnum.Documents);
  const showReviewRules =
    isEditMode && !!id && hasReviewsFeature && documentsPerms.update;

  const {
    data: ruleData,
    isLoading: isRulesLoading,
    error: rulesError,
  } = useGetDocumentReviewRulesForFolderQuery(id!, {skip: !showReviewRules});

  const [createRule] = useCreateDocumentReviewRuleMutation();
  const [updateRule] = useUpdateDocumentReviewRuleMutation();
  const [deleteRule] = useDeleteDocumentReviewRuleMutation();

  const [ruleStates, setRuleStates] = useState<
    Record<ReviewRuleType, ReviewRuleEditState>
  >(() => ({
    [DocumentReviewTypeEnum.Approval]: buildInitialRuleState(id ?? 0),
    [DocumentReviewTypeEnum.Acknowledgment]: buildInitialRuleState(id ?? 0),
  }));
  // Server-seeded baseline per type, used to (a) detect whether the user
  // actually touched a rule before persisting it on global save, and (b)
  // restore a card's local edits when leaving override mode.
  const [ruleSeeds, setRuleSeeds] = useState<
    Record<ReviewRuleType, ReviewRuleEditState>
  >(() => ({
    [DocumentReviewTypeEnum.Approval]: buildInitialRuleState(id ?? 0),
    [DocumentReviewTypeEnum.Acknowledgment]: buildInitialRuleState(id ?? 0),
  }));
  const [ruleErrors, setRuleErrors] = useState<
    Partial<Record<ReviewRuleType, string>>
  >({});
  const [confirmRemoveType, setConfirmRemoveType] = useState<
    ReviewRuleType | undefined
  >();

  useEffect(() => {
    if (rulesError) {
      setAlert(parseErrorMessage(rulesError), 'error');
    }
  }, [rulesError, setAlert]);

  // Read via refs (rather than listing in the effect below's deps) so a
  // refetch triggered by one type's mutation — both `createRule`/`updateRule`
  // invalidate `DocumentReviewRules` — can be checked against each type's
  // latest dirty state without re-running the effect on every keystroke.
  const ruleStatesRef = useRef(ruleStates);
  useEffect(() => {
    ruleStatesRef.current = ruleStates;
  }, [ruleStates]);
  const ruleSeedsRef = useRef(ruleSeeds);
  useEffect(() => {
    ruleSeedsRef.current = ruleSeeds;
  }, [ruleSeeds]);

  useEffect(() => {
    if (!showReviewRules || isRulesLoading || !id) {
      return;
    }
    // Reseed per type, skipping any type the user is actively editing (or
    // whose save just failed), or that is mid override-configuration, so a
    // refetch triggered by the OTHER type's save/removal never reverts this
    // type's unsaved edits or collapses its in-progress override session.
    RULE_TYPES.forEach(reviewType => {
      const currentState = ruleStatesRef.current[reviewType];
      const currentSeed = ruleSeedsRef.current[reviewType];
      if (
        currentState.overrideMode ||
        isRuleStateDirty(currentState, currentSeed)
      ) {
        return;
      }
      const seeded = buildInitialRuleState(
        id,
        effectiveRuleForType(ruleData, reviewType)
      );
      setRuleStates(prev => ({...prev, [reviewType]: seeded}));
      setRuleSeeds(prev => ({...prev, [reviewType]: seeded}));
    });
  }, [showReviewRules, isRulesLoading, id, ruleData]);

  const handleRuleChange = (
    reviewType: ReviewRuleType,
    patch: Partial<ReviewRuleEditState>
  ) => {
    setRuleStates(prev => ({
      ...prev,
      [reviewType]: {...prev[reviewType], ...patch},
    }));
    setRuleErrors(prev => ({...prev, [reviewType]: undefined}));
  };

  const handleCancelOverride = (reviewType: ReviewRuleType) => {
    setRuleStates(prev => ({...prev, [reviewType]: ruleSeeds[reviewType]}));
    setRuleErrors(prev => ({...prev, [reviewType]: undefined}));
  };

  // Entering override mode seeds the card from the inherited rule's actual
  // values (rather than the blank defaults `buildInitialRuleState` produces
  // for a non-own rule), so tweaking the parent's rule doesn't force the
  // user to reconstruct it from scratch. The seed is written to both
  // `ruleStates` and `ruleSeeds` (baseline) so `isRuleStateDirty` reports
  // "not dirty" until the user actually changes something — entering
  // override mode and saving immediately must persist no rule.
  const handleEnterOverride = (reviewType: ReviewRuleType) => {
    const seededValues = buildOverrideSeedValues(
      effectiveRuleForType(ruleData, reviewType)
    );
    setRuleStates(prev => ({
      ...prev,
      [reviewType]: {
        ...prev[reviewType],
        ...seededValues,
        overrideMode: true,
        ownRuleId: undefined,
      },
    }));
    setRuleSeeds(prev => ({
      ...prev,
      [reviewType]: {
        ...prev[reviewType],
        ...seededValues,
        overrideMode: false,
        ownRuleId: undefined,
      },
    }));
  };

  const handleConfirmRemoveRule = async () => {
    const reviewType = confirmRemoveType;
    setConfirmRemoveType(undefined);
    if (reviewType === undefined) return;

    const ownRuleId = ruleStates[reviewType].ownRuleId;
    if (!ownRuleId) return;

    try {
      await deleteRule(ownRuleId).unwrap();
      const resetState: ReviewRuleEditState = {
        overrideMode: false,
        ownRuleId: undefined,
        enabled: false,
        completionMode: DocumentReviewCompletionModeEnum.AnyOneApprover,
        sourceEntities: {entities: []},
      };
      setRuleStates(prev => ({...prev, [reviewType]: resetState}));
      setRuleSeeds(prev => ({...prev, [reviewType]: resetState}));
      setAlert(
        `${DocumentReviewTypeLabel(reviewType)} rule was successfully removed`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const validateRuleStates = (): boolean => {
    const errors: Partial<Record<ReviewRuleType, string>> = {};
    RULE_TYPES.forEach(reviewType => {
      const effective = effectiveRuleForType(ruleData, reviewType);
      const state = ruleStates[reviewType];
      const editable = isOwnEditable(id ?? 0, effective, state);
      const dirty = isRuleStateDirty(state, ruleSeeds[reviewType]);
      if (
        editable &&
        dirty &&
        state.enabled &&
        (state.sourceEntities.entities?.length ?? 0) === 0
      ) {
        errors[reviewType] = 'Reviewers is required';
      }
    });
    setRuleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveRule = async (reviewType: ReviewRuleType, folderId: number) => {
    const effective = effectiveRuleForType(ruleData, reviewType);
    const state = ruleStates[reviewType];
    if (!isOwnEditable(folderId, effective, state)) {
      return;
    }
    // The user never actually changed this rule from its server-seeded
    // baseline — skip it so the global save never materializes a rule the
    // user only "peeked at" (e.g. via "Configure for this folder").
    if (!isRuleStateDirty(state, ruleSeeds[reviewType])) {
      return;
    }

    const request = {
      folderId,
      reviewType,
      completionMode: state.completionMode,
      active: state.enabled,
      sourceEntities: state.sourceEntities.entities ?? [],
    };

    const response = state.ownRuleId
      ? await updateRule({id: state.ownRuleId, ...request}).unwrap()
      : await createRule(request).unwrap();

    const savedState: ReviewRuleEditState = {
      ...state,
      ownRuleId: response.id,
      overrideMode: false,
    };
    setRuleStates(prev => ({...prev, [reviewType]: savedState}));
    setRuleSeeds(prev => ({...prev, [reviewType]: savedState}));
  };

  const handleSave = async () => {
    setIsSavePending(true);
    try {
      const values = form.methods.getValues();
      if (isCreateMode) {
        const model = MapDocumentResponseToDocumentCreateRequest(
          values as DocumentResponse
        );
        model.isFolder = true;
        model.parentId =
          parentId && parentId !== '0' ? Number(parentId) : undefined;
        const response = await createDocumentNode(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
        navigate(PathUtility.DocumentsManagerList(response.id), {
          replace: true,
        });
      } else {
        const model = MapDocumentResponseToDocumentUpdateRequest({
          ...values,
          id,
          version: fetchedDocument?.version,
        } as DocumentResponse);
        const response = await updateDocument(model).unwrap();

        // Each type is saved independently — a rejected/errored settle for
        // one type must not be reported as if the whole save failed, and
        // must not prevent the other (possibly already-succeeded) type from
        // being reported and left persisted.
        let ruleFailures: {reviewType: ReviewRuleType; error: unknown}[] = [];
        if (showReviewRules && id) {
          const results = await Promise.allSettled(
            RULE_TYPES.map(reviewType => saveRule(reviewType, id))
          );
          ruleFailures = RULE_TYPES.flatMap((reviewType, index) => {
            const result = results[index];
            return result.status === 'rejected'
              ? [{reviewType, error: result.reason}]
              : [];
          });
        }

        if (ruleFailures.length > 0) {
          const failureDetail = ruleFailures
            .map(
              ({reviewType, error}) =>
                `${DocumentReviewTypeLabel(
                  reviewType
                )} rule — ${parseErrorMessage(error)}`
            )
            .join('; ');
          setAlert(
            `${response.name} was updated, but the following review rules failed to save: ${failureDetail}`,
            'error'
          );
        } else {
          setAlert(`${response.name} was successfully updated`, 'success');
          navigate(PathUtility.DocumentsManagerList(id), {replace: true});
        }
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isFormValid = await form.validateForm();
    const isRulesValid = validateRuleStates();
    if (isFormValid && isRulesValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.DocumentsManagerList(id)
      : PathUtility.DocumentsManagerList(
          parentId && parentId !== '0' ? Number(parentId) : 0
        )
  );

  const ancestors = useDocumentAncestors(id || parentId, parentId);

  const name = useWatch({control: form.methods.control, name: 'name'});

  const {breadcrumbs, fallbackTo} = useDocumentManagementBreadcrumbs({
    mode: isCreateMode ? 'create-folder' : 'edit-folder',
    nodeId: id,
    parentId: parentId,
    nodeName: name as string,
    ancestors,
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkDocumentManager.icon}>
          {isCreateMode ? 'New Folder' : 'Edit Folder'}
        </PageHeader.Title>
        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <SectionCard
          title="Folder Details"
          data-testid="section-folder-details">
          <Box p="md" maw={500}>
            <Grid gutter="md">
              <ProactField name="name" />
            </Grid>
          </Box>
        </SectionCard>
      </ProactForm>

      {isEditMode && id && hasReviewsFeature && documentsPerms.update && (
        <>
          {isRulesLoading ? (
            <Center py="md" data-testid="review-rules-loading">
              <Loader size="sm" />
            </Center>
          ) : (
            <SimpleGrid
              cols={{base: 1, md: 2}}
              spacing="md"
              data-testid="review-rules-section">
              {RULE_TYPES.map(reviewType => (
                <ReviewRuleCard
                  key={reviewType}
                  folderId={id}
                  reviewType={reviewType}
                  effective={effectiveRuleForType(ruleData, reviewType)}
                  canEdit={documentsPerms.update && !isSavePending}
                  state={ruleStates[reviewType]}
                  error={ruleErrors[reviewType]}
                  onChange={patch => handleRuleChange(reviewType, patch)}
                  onRequestRemove={() => setConfirmRemoveType(reviewType)}
                  onCancelOverride={() => handleCancelOverride(reviewType)}
                  onEnterOverride={() => handleEnterOverride(reviewType)}
                />
              ))}
            </SimpleGrid>
          )}
        </>
      )}

      <ConfirmDialog
        opened={confirmRemoveType !== undefined}
        onClose={() => setConfirmRemoveType(undefined)}
        onConfirm={handleConfirmRemoveRule}
        confirmationButtonColor="red"
        title={`Are you sure you want to remove the ${
          confirmRemoveType ? DocumentReviewTypeLabel(confirmRemoveType) : ''
        } rule for this folder?`}
        body={`The ${
          confirmRemoveType ? DocumentReviewTypeLabel(confirmRemoveType) : ''
        } rule configured for this folder will be deleted. This folder will fall back to the rule inherited from its parent, if any.`}
      />
    </Stack>
  );
};
